// Rug Room Shots: "Check with AI" (SPEC_AICHECK 4.7 / 4.9 / 5.5). Queues AI checks of many images of one job:
//   * "Images not checked yet" (selection.mode 'unchecked', the default): every current finished image of the job
//     with no AI result and no open check;
//   * "The N selected images" (the ticked rows, { mode: 'ids' }), or with "Select all" active "Every image in this
//     view" (the Review filter, { mode: 'filter' });
//   * "Check again: every image in this view" (the Review filter + recheck).
// 1. POST jobs/{id}/ai-checks/preview (read-only) on open and on every option change. Confirm stays disabled until
//    the preview on screen was made with the options on screen (previewKey), as in the bulk regenerate dialog.
// 2. POST jobs/{id}/ai-checks commits exactly preview.genIds with expectedCount / expectedUsd, which the backend
//    re-checks (409 selection_changed carries a fresh preview). clientRef is made once per open: a repeat is refused
//    with 409 already_submitted and the first result, which counts as success.
// "Stop queued checks" (POST jobs/{id}/ai-checks/cancel) stops the job's checks still waiting; running ones finish.
// The parent mounts this dialog only while it is open, so every open starts fresh with a new clientRef.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, CircularProgress, Stack, Typography,
  FormControlLabel, Checkbox, RadioGroup, Radio, Chip, Box,
} from '@mui/material';
import { MdStopCircle } from 'react-icons/md';
import rrsApi from './api';
import {
  primaryButtonSx, fmtUsd, fmtInt, errText, serverText, conflictCode, isDailyLimit, dailyLimitText, overBudget,
  overBudgetText, signalSpend, ConfirmDialog, AI_NOT_READY_TEXT, aiNextPriceText,
} from './common';

const makeClientRef = () => `ac-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;

const TIMEOUT_TEXT = 'The server did not answer in time. It may still have queued the checks. The list has been refreshed. '
  + 'Confirming again is safe: a repeat is refused if the first one went through.';

const REVIEW_CHIPS = [
  { key: 'pending', label: 'waiting for review' },
  { key: 'approved', label: 'approved' },
  { key: 'rejected', label: 'rejected' },
];

// Skip reasons in the backend's order (SPEC_AICHECK 4.1 AiCheckSkipReasons).
const skipText = (mode) => ({
  not_found: (n) => `${n} no longer exist`,
  other_job: (n) => `${n} belong to another job`,
  not_succeeded: (n) => `${n} have no finished image`,
  outputs_not_ready: (n) => `${n} have files that are not ready yet`,
  not_latest: (n) => `${n} have a newer attempt`,
  not_pending: (n) => `${n} are already approved or rejected (untick Only images waiting for review to include them)`,
  checking: (n) => `${n} are already being checked`,
  already_checked: (n) => (mode === 'selected'
    ? `${n} are already checked (tick Check again to include them)`
    : `${n} are already checked (choose Check again to include them)`),
});
const SKIP_ORDER = ['not_found', 'other_job', 'not_succeeded', 'outputs_not_ready', 'not_latest', 'not_pending', 'checking', 'already_checked'];

const AiCheckDialog = ({
  open, onClose, jobId, selection, selectAll, total, filter, config, notify, onDone, onRefresh, queuedCount,
}) => {
  const featureOn = !!(config && config.features && config.features.aiCheck);
  const hasSelection = !!(selection && ((selection.mode === 'ids' && selection.genIds && selection.genIds.length)
    || selection.mode === 'filter'));
  // unchecked | selected | recheck. Opened with ticked rows, the ticked rows come first (they are what the person
  // just picked); otherwise "Images not checked yet". Either way nothing is queued before the person confirms.
  const [mode, setMode] = useState(() => (hasSelection ? 'selected' : 'unchecked'));
  const [onlyPending, setOnlyPending] = useState(true);
  const [recheckSelected, setRecheckSelected] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [changedMsg, setChangedMsg] = useState('');
  const [timeoutMsg, setTimeoutMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [stopAsk, setStopAsk] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);
  const [clientRef] = useState(makeClientRef);
  const seq = useRef(0);

  // The request for the options on screen.
  const request = useMemo(() => {
    if (mode === 'selected' && hasSelection) {
      return { selection, recheck: !!recheckSelected, onlyPending };
    }
    if (mode === 'recheck') {
      return { selection: { mode: 'filter', filter }, recheck: true, onlyPending };
    }
    return { selection: { mode: 'unchecked' }, recheck: false, onlyPending };
  }, [mode, hasSelection, selection, recheckSelected, onlyPending, filter]);
  const requestKey = useMemo(() => JSON.stringify(request), [request]);

  const loadPreview = useCallback(async () => {
    if (!jobId) return;
    const my = seq.current + 1;
    seq.current = my;
    const usedKey = requestKey;
    setLoading(true);
    setError('');
    try {
      const res = await rrsApi.aiCheckPreview(jobId, request);
      if (my !== seq.current) return;
      setPreview(res);
      setPreviewKey(usedKey);
    } catch (e) {
      if (my === seq.current) {
        setError(serverText(e));
        if (conflictCode(e) === 'worker_not_ready') setPreview(null);
      }
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [jobId, request, requestKey]);

  useEffect(() => { if (open) loadPreview(); }, [open, loadPreview]);

  const count = preview ? Number(preview.count) || 0 : 0;
  const requested = preview ? Number(preview.requested) || 0 : 0;
  const estUsd = preview ? Number(preview.estUsd) || 0 : 0;
  const skipped = (preview && preview.skippedCounts) || {};
  const byReview = (preview && preview.byReviewState) || {};
  const fresh = !!preview && previewKey === requestKey;
  const nothing = !!preview && fresh && !loading && count === 0;
  const canConfirm = featureOn && !!preview && fresh && !loading && !busy && count > 0;
  const texts = skipText(mode);

  const finish = (res) => {
    const r = res || {};
    const n = r.queued !== undefined && r.queued !== null ? r.queued : r.count;
    const usd = r.estUsd !== undefined && r.estUsd !== null ? r.estUsd : estUsd;
    signalSpend();
    notify(
      `Queued ${fmtInt(n)} AI check${Number(n) === 1 ? '' : 's'} (about ${fmtUsd(usd)}). Results appear on each image as they finish.`
      + `${r.generationPaused ? ' Generation is paused: the checks wait until it is resumed.' : ''}`,
      'success',
    );
    if (onDone) onDone(r);
  };

  const submit = async () => {
    if (!canConfirm) return;
    setBusy(true);
    setChangedMsg('');
    setError('');
    setTimeoutMsg('');
    try {
      const res = await rrsApi.aiCheckQueue(jobId, {
        clientRef,
        genIds: preview.genIds || [],
        expectedCount: preview.count,
        expectedUsd: preview.estUsd,
        recheck: request.recheck,
        onlyPending: request.onlyPending,
        selection: request.selection,
      });
      finish(res);
    } catch (e) {
      const code = conflictCode(e);
      if (code === 'already_submitted') {
        finish((e.body && e.body.current) || {});
      } else if (code === 'selection_changed' && e.body && e.body.current) {
        const cur = e.body.current;
        setPreview(cur);
        setPreviewKey(requestKey);
        setChangedMsg(`The selection changed: now ${fmtInt(cur.count)} images. Check and confirm again.`);
      } else if (code === 'worker_not_ready') {
        notify(serverText(e), 'warning');
        setError(serverText(e));
      } else if (isDailyLimit(e)) {
        // Over the non-admin daily limit: nothing was queued. The server's words; no automatic retry.
        setError(dailyLimitText(e));
        signalSpend();
      } else if (!e || !e.status || e.status >= 500) {
        // Possibly a load-balancer timeout while the commit still went through: refresh both and keep the same
        // clientRef, so confirming again cannot queue a second set.
        setTimeoutMsg(TIMEOUT_TEXT);
        if (onRefresh) await onRefresh();
        loadPreview();
      } else if (code) {
        // nothing_eligible and other refusals: the server's words, then a fresh preview.
        setError(serverText(e));
        loadPreview();
      } else {
        setError(errText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const stopQueued = async () => {
    setStopBusy(true);
    try {
      const res = await rrsApi.aiCheckCancel(jobId);
      const n = res && res.cancelled !== undefined ? res.cancelled : 0;
      notify(`Stopped ${fmtInt(n)} AI check${Number(n) === 1 ? '' : 's'}.`, 'info');
      setStopAsk(false);
      if (onRefresh) await onRefresh();
      loadPreview();
    } catch (e) {
      notify(serverText(e), conflictCode(e) ? 'warning' : 'error');
    } finally {
      setStopBusy(false);
    }
  };

  const selectedLabel = selectAll
    ? `Every image in this view (${fmtInt(total || 0)})`
    : `The ${fmtInt(selection && selection.genIds ? selection.genIds.length : 0)} selected images`;
  const confirmLabel = `Check ${fmtInt(count)} image${count === 1 ? '' : 's'} · about ${fmtUsd(estUsd)}`;
  const queued = Number(queuedCount) || 0;
  // Google's next dated price for the checker (the worker's check.price_changes), e.g. "about $0.045 from Dec 1, 2026".
  const nextPrice = aiNextPriceText(config && config.aiCheck && config.aiCheck.usdPerCheckNext);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Check with AI
        <Typography variant="body2" color="text.secondary">
          Job #{jobId}. The AI compares each image with the product photo, the rug&apos;s close-up and the original room,
          and gives a score, Pass / Needs a look / Fail and the reason. It never approves, rejects or regenerates anything.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {!featureOn && <Alert severity="info" sx={{ mb: 1.5 }}>{AI_NOT_READY_TEXT}</Alert>}
        {changedMsg && <Alert severity="warning" sx={{ mb: 1.5 }}>{changedMsg}</Alert>}
        {timeoutMsg && <Alert severity="warning" sx={{ mb: 1.5 }}>{timeoutMsg}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Which images</Typography>
        <RadioGroup value={mode} onChange={(e) => setMode(e.target.value)}>
          <FormControlLabel value="unchecked" control={<Radio size="small" />} label="Images not checked yet" disabled={busy} />
          {hasSelection && (
            <FormControlLabel value="selected" control={<Radio size="small" />} label={selectedLabel} disabled={busy} />
          )}
          {mode === 'selected' && hasSelection && (
            <FormControlLabel
              sx={{ ml: 4 }}
              control={<Checkbox size="small" checked={recheckSelected} onChange={(e) => setRecheckSelected(e.target.checked)} disabled={busy} />}
              label={<Typography variant="body2">Check again images that already have an AI result</Typography>}
            />
          )}
          <FormControlLabel value="recheck" control={<Radio size="small" />} label="Check again: every image in this view" disabled={busy} />
        </RadioGroup>
        <FormControlLabel
          control={<Checkbox size="small" checked={onlyPending} onChange={(e) => setOnlyPending(e.target.checked)} disabled={busy} />}
          label="Only images waiting for review"
        />

        <Box sx={{ mt: 1.5 }}>
          {loading && !preview && <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
          {preview && (
            <>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {fmtInt(count)} image{count === 1 ? '' : 's'} · about {fmtUsd(estUsd)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({fmtUsd(preview.usdPerCheck, 3)} each{preview.model ? `, ${preview.model}` : ''}
                  {nextPrice ? `; ${nextPrice}` : ''})
                </Typography>
                {loading && <CircularProgress size={16} />}
              </Stack>
              {count > 0 && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {REVIEW_CHIPS.filter((c) => Number(byReview[c.key]) > 0).map((c) => (
                    <Chip key={c.key} size="small" variant="outlined" label={`${fmtInt(byReview[c.key])} ${c.label}`} />
                  ))}
                </Stack>
              )}
              {requested - count > 0 && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {fmtInt(requested - count)} will be skipped:
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {SKIP_ORDER.filter((k) => Number(skipped[k]) > 0).map((k) => (
                      <li key={k}><Typography variant="body2">{texts[k](fmtInt(skipped[k]))}</Typography></li>
                    ))}
                  </Box>
                </Alert>
              )}
              {nothing && (
                <Typography color="text.secondary" sx={{ mt: 1.5 }}>Nothing to check in this selection.</Typography>
              )}
              {preview.generationPaused && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>Generation is paused: the checks will wait until it is resumed.</Alert>
              )}
              {!error && count > 0 && overBudget(config, estUsd) && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>{overBudgetText(config, estUsd)}</Alert>
              )}
            </>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
          Results appear on each image as they finish. Every check is logged in the Spend &amp; log tab.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {queued > 0 && (
            <Button color="warning" startIcon={<MdStopCircle />} onClick={() => setStopAsk(true)} disabled={busy || stopBusy}>
              Stop queued checks ({fmtInt(queued)})
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose} disabled={busy}>{nothing ? 'Close' : 'Cancel'}</Button>
          {!nothing && (
            <Button variant="contained" onClick={submit} disabled={!canConfirm} sx={primaryButtonSx}>
              {busy ? <CircularProgress size={20} color="inherit" /> : (preview ? confirmLabel : 'Check')}
            </Button>
          )}
        </Stack>
      </DialogActions>

      <ConfirmDialog
        open={stopAsk} busy={stopBusy} onClose={() => setStopAsk(false)} onConfirm={stopQueued}
        title="Stop queued checks?" confirmLabel="Stop" confirmColor="warning"
      >
        <Typography>
          Stop the {fmtInt(queued)} AI check{queued === 1 ? '' : 's'} still waiting? Checks already running finish.
        </Typography>
      </ConfirmDialog>
    </Dialog>
  );
};

export default AiCheckDialog;
